<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { Icon } from '@iconify/vue';
import { PerfectScrollbar } from 'vue3-perfect-scrollbar';
import { NotificationsService } from '@/services/notifications.service';
import { useAuthStore } from '@/stores/authStore';
import type { NotificationResponse } from '@/types/api';

const authStore = useAuthStore();

const notifications = ref<NotificationResponse[]>([]);
const loading = ref(false);
const error = ref<string | null>(null);

const unreadCount = computed(() =>
  notifications.value.filter((item) => !item.read).length,
);

const typeIconMap: Record<string, string> = {
  info: 'solar:bell-line-duotone',
  success: 'solar:check-line-duotone',
  warning: 'solar:warning-line-duotone',
  error: 'solar:close-circle-line-duotone',
};

function formatTime(createdAt?: string) {
  if (!createdAt) {
    return '—';
  }

  const date = new Date(createdAt);
  return date.toLocaleString([], { hour: '2-digit', minute: '2-digit' });
}

async function loadNotifications() {
  // Notifications are organization-scoped (reservations, payments...) —
  // a super_admin has no organization_id and the endpoint 403s for them.
  if (!authStore.user?.organization_id) {
    return;
  }
  loading.value = true;
  error.value = null;
  try {
    notifications.value = await NotificationsService.getMyNotifications();
  } catch (err) {
    console.error(err);
    error.value = 'Impossible de récupérer les notifications';
  } finally {
    loading.value = false;
  }
}

onMounted(loadNotifications);
</script>

<template>
  <v-menu offset-y>
    <template #activator="{ props }">
      <v-btn
        icon
        variant="text"
        size="small"
        class="custom-hover-primary"
        v-bind="props"
      >
        <v-badge
          :content="unreadCount"
          color="error"
          :value="unreadCount > 0"
        >
          <Icon
            icon="solar:bell-line-duotone"
            height="22"
          />
        </v-badge>
      </v-btn>
    </template>

    <v-card
      width="350"
      class="notification-dropdown"
    >
      <v-card-title class="px-4 py-3">
        <span class="text-h6">Notifications</span>
        <v-spacer />
        <v-btn
          variant="text"
          size="small"
          color="primary"
          :disabled="loading"
          @click="loadNotifications"
        >
          Rafraîchir
        </v-btn>
      </v-card-title>

      <v-divider />

      <PerfectScrollbar style="max-height: 320px">
        <v-list
          density="compact"
          class="py-0"
        >
          <template v-if="error">
            <v-list-item>
              <v-list-item-title class="text-caption text-error">
                {{ error }}
              </v-list-item-title>
            </v-list-item>
          </template>
          <template v-else-if="!notifications.length && !loading">
            <v-list-item>
              <v-list-item-title class="text-caption text-disabled">
                Aucune notification
              </v-list-item-title>
            </v-list-item>
          </template>
          <v-list-item
            v-for="notification in notifications"
            :key="notification.id"
            :class="{ 'notification-unread': !notification.read }"
          >
            <template #prepend>
              <v-avatar
                size="40"
                class="mr-3"
                color="primary"
              >
                <Icon
                  :icon="typeIconMap[notification.type] ?? 'solar:bell-line-duotone'"
                  height="20"
                />
              </v-avatar>
            </template>
            <div class="notification-body">
              <span class="text-body-2 font-weight-medium">{{ notification.message }}</span>
              <div class="text-caption text-medium-emphasis mt-1">
                {{ formatTime(notification.created_at) }}
              </div>
            </div>
          </v-list-item>
        </v-list>
      </PerfectScrollbar>

      <v-divider />

      <v-card-actions class="px-4 py-2">
        <v-btn
          variant="text"
          color="primary"
          block
        >
          Voir toutes les notifications
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-menu>
</template>

<style lang="scss" scoped>
.custom-hover-primary {
  &:hover {
    background-color: rgb(var(--v-theme-lightprimary));
    color: rgb(var(--v-theme-primary));
  }
}

.notification-dropdown {
  .notification-unread {
    background-color: rgba(var(--v-theme-primary), 0.08);
  }

  .notification-body {
    display: flex;
    flex-direction: column;
  }
}
</style>
